import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMasterData } from '../../hooks/useMasterData';
import { useAuth } from '../../hooks/useAuth';
import { useAuthorization } from '../../hooks/useAuthorization';
import { useCaseHelpers } from '../../hooks/useCaseHelpers';
import { useToast } from '../../hooks/useToast';
import { Role, DEFAULT_DRIVER_TRANSPORT_FEE_VISIBILITY_SETTINGS } from '../../types';
import { formatTimeToHHMM } from '../../utils/timeFormatter';
import logger from '../../utils/logger';
import {
  ArrowLeftIcon,
  BuildingOffice2Icon,
  CalendarDaysIcon,
  DocumentTextIcon,
  TruckIcon,
  UserIcon,
  PencilIcon,
  ExclamationTriangleIcon,
  CurrencyYenIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { getCaseStatusText } from '../../utils/statusUtils';
import { renderVehiclePlateNumber } from '../../utils/vehiclePlateNumberDisplay';
import { formatCurrencySafe } from '../../utils/currencyFormatter';
import { Button, Badge, Card, PageTitle } from '../../components/ui';
import { VehicleInspectionStatus } from '../../components/ui/VehicleInspectionStatus';
import { DeleteConfirmModal } from '../../components/ui/DeleteConfirmModal';
import { supabaseCompanyService } from '../../services/supabase/supabaseCompanyService';

const DetailItem: React.FC<{
  icon: React.ReactNode;
  label: string;
  value?: string | React.ReactNode;
}> = ({ icon, label, value }) => (
  <div className='flex items-start'>
    <div className='flex-shrink-0 w-8 text-gray-400'>{icon}</div>
    <div className='ml-4'>
      <dt className='text-sm font-medium text-gray-500 dark:text-gray-400'>{label}</dt>
      <dd className='mt-1 text-sm text-gray-900 dark:text-white break-words'>{value || 'N/A'}</dd>
    </div>
  </div>
);

const DetailCard: React.FC<{ title: string; children: React.ReactNode; icon: React.ReactNode }> = ({
  title,
  children,
  icon,
}) => (
  <Card className='p-6'>
    <Card.Header className='mb-4 pb-4 border-b border-gray-200 dark:border-gray-700'>
      <h3 className='text-lg font-semibold text-gray-800 dark:text-white flex items-center'>
        {icon}
        <span className='ml-3'>{title}</span>
      </h3>
    </Card.Header>
    <Card.Content>
      <dl className='space-y-4'>{children}</dl>
    </Card.Content>
  </Card>
);

// ステータス表示関数は utils/statusUtils.ts に統一

export const CaseDetailPage: React.FC = () => {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { checkPermission } = useAuthorization();
  const {
    safe: { getVehicleName, getPartnerNameByRole, getTransporterName },
  } = useCaseHelpers();
  const { error: showError } = useToast();

  // FL-229: ドライバー表示設定を確認するため、会社設定を取得
  const [showTransportFeeToDriver, setShowTransportFeeToDriver] = useState(false);

  // 戻るボタンの動的判定：前のページへ戻るか、デフォルトで案件一覧へ
  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/cases');
    }
  };

  // 権限チェック
  const canEditCase = checkPermission([Role.ADMIN, Role.DISPATCHER]);
  // FL-208: 陸送員は料金情報を表示しない
  // FL-229: ドライバーの場合、会社設定でも確認
  const canViewPricing = (() => {
    const hasBasePermission = checkPermission([
      Role.SUPER_ADMIN,
      Role.ADMIN,
      Role.DISPATCHER,
      Role.READ_ONLY,
    ]);

    // ドライバーの場合、会社設定をチェック
    if (!hasBasePermission && user?.role === Role.DRIVER) {
      return showTransportFeeToDriver;
    }

    return hasBasePermission;
  })();
  const {
    cases,
    /* vehicles, businessPartners, getVehicleById, */
    getVehicleModelById,
    getVehicleTypeById,
    getBusinessPartnerById,
    getBusinessPartnerLocationById,
    refreshData,
    deleteCase,
  } = useMasterData();

  // 削除確認モーダルの状態管理
  const [deleteConfirm, setDeleteConfirm] = useState<{
    show: boolean;
    caseId: string;
    vehicleName: string;
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // FL-229: ドライバー表示設定を取得
  useEffect(() => {
    const fetchDriverVisibilitySettings = async () => {
      if (!user?.companyId) {
        logger.warn('[CaseDetailPage] ユーザーの会社IDが見つかりません');
        return;
      }

      try {
        const settings = await supabaseCompanyService.getDriverTransportFeeVisibilitySettings(
          user.companyId
        );
        setShowTransportFeeToDriver(settings.showTransportFeeToDriver);
        logger.info(
          '[CaseDetailPage] ドライバー表示設定取得完了',
          settings.showTransportFeeToDriver
        );
      } catch (error) {
        logger.error('[CaseDetailPage] ドライバー表示設定取得失敗:', error);
        // デフォルト値を使用
        setShowTransportFeeToDriver(
          DEFAULT_DRIVER_TRANSPORT_FEE_VISIBILITY_SETTINGS.showTransportFeeToDriver
        );
      }
    };

    fetchDriverVisibilitySettings();
  }, [user?.companyId]);

  // ページマウント時にデータを更新
  useEffect(() => {
    logger.info('[CaseDetailPage] コンポーネントマウント - データ取得開始');
    refreshData();
  }, [refreshData]);

  // caseIdが変更された時にもデータを更新
  useEffect(() => {
    if (caseId) {
      logger.info('[CaseDetailPage] caseId変更 - データ取得:', caseId);
      refreshData();
    }
  }, [caseId, refreshData]);

  // ページがフォーカスされた時にもデータを更新（他のページで変更があった場合）
  useEffect(() => {
    const handleFocus = () => {
      logger.info('[CaseDetailPage] ウィンドウフォーカス - データ更新');
      refreshData();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [refreshData]);

  const caseData = cases.find(c => c.id === caseId);

  // デバッグ用：案件データの状況をログ出力
  useEffect(() => {
    logger.info('[CaseDetailPage] 案件データ検索:', {
      caseId,
      totalCases: cases.length,
      caseData: caseData
        ? {
            id: caseData.id,
            assignedLandTransporterId: caseData.assignedLandTransporterId,
            status: caseData.status,
          }
        : null,
    });
  }, [caseId, cases, caseData]);

  if (!caseData) {
    return (
      <div className='text-center py-12'>
        <DocumentTextIcon className='mx-auto h-12 w-12 text-gray-400' />
        <h3 className='mt-2 text-sm font-medium text-gray-900 dark:text-white'>
          案件が見つかりません
        </h3>
        <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
          指定された案件IDが存在しません。
        </p>
        <Button onClick={handleGoBack} variant='primary' className='mt-4'>
          戻る
        </Button>
      </div>
    );
  }

  // const getClientName = (clientId: string) => {
  //     const client = clients.find(c => c.id === clientId);
  //     return client?.companyName || '不明な顧客';
  // };

  // FL-132: 取引先から住所・電話番号を自動取得する機能
  const getPickupAddress = () => {
    if (caseData.pickupAddress) return caseData.pickupAddress;
    if (!caseData.pickupPartnerId) return '';

    const partner = getBusinessPartnerById(caseData.pickupPartnerId);
    if (!partner) return '';

    if (caseData.pickupLocationId) {
      const location = getBusinessPartnerLocationById(
        caseData.pickupPartnerId,
        caseData.pickupLocationId
      );
      if (location) return location.address;
    }

    return partner.defaultAddress || '';
  };

  const getPickupPhone = () => {
    if (caseData.pickupPhone) return caseData.pickupPhone;
    if (!caseData.pickupPartnerId) return '';

    const partner = getBusinessPartnerById(caseData.pickupPartnerId);
    if (!partner) return '';

    if (caseData.pickupLocationId) {
      const location = getBusinessPartnerLocationById(
        caseData.pickupPartnerId,
        caseData.pickupLocationId
      );
      if (location) return location.phone;
    }

    return partner.defaultPhone || '';
  };

  const getDeliveryAddress = () => {
    if (caseData.deliveryAddress) return caseData.deliveryAddress;
    if (!caseData.deliveryPartnerId) return '';

    const partner = getBusinessPartnerById(caseData.deliveryPartnerId);
    if (!partner) return '';

    if (caseData.deliveryLocationId) {
      const location = getBusinessPartnerLocationById(
        caseData.deliveryPartnerId,
        caseData.deliveryLocationId
      );
      if (location) return location.address;
    }

    return partner.defaultAddress || '';
  };

  const getDeliveryPhone = () => {
    if (caseData.deliveryPhone) return caseData.deliveryPhone;
    if (!caseData.deliveryPartnerId) return '';

    const partner = getBusinessPartnerById(caseData.deliveryPartnerId);
    if (!partner) return '';

    if (caseData.deliveryLocationId) {
      const location = getBusinessPartnerLocationById(
        caseData.deliveryPartnerId,
        caseData.deliveryLocationId
      );
      if (location) return location.phone;
    }

    return partner.defaultPhone || '';
  };

  // 削除ボタンクリック時のハンドラー
  const handleDeleteCase = (id: string, vehicleName: string) => {
    setDeleteConfirm({ show: true, caseId: id, vehicleName });
  };

  // 削除確認後の実行ハンドラー
  const confirmDeleteCase = async () => {
    if (!deleteConfirm) return;

    try {
      setIsDeleting(true);
      await deleteCase(deleteConfirm.caseId);
      logger.info('✅ 案件削除成功:', deleteConfirm.vehicleName);
      setDeleteConfirm(null);

      // 案件一覧ページへ遷移
      navigate('/cases');
    } catch (error) {
      logger.error('❌ 案件削除失敗:', error);
      showError('案件の削除に失敗しました。もう一度お試しください。');
    } finally {
      setIsDeleting(false);
    }
  };

  const getVehicleType = () => {
    // 1. caseData.vehicleTypeが直接設定されている場合はそれを使用（後方互換性）
    if (caseData.vehicleType) {
      return caseData.vehicleType;
    }

    // 2. vehicleModelId（UUID）が設定されている場合 - 最優先
    if (caseData.vehicleModelId) {
      const model = getVehicleModelById(caseData.vehicleModelId);
      if (model) {
        const vehicleType = getVehicleTypeById(model.vehicleTypeId);
        return vehicleType?.name || '車種不明';
      }
    }

    // 3. レガシー対応: vehicleModel === 'custom'の場合
    if (caseData.vehicleModel === 'custom') {
      const typeId = (caseData as any).vehicleTypeId;
      if (typeId) {
        const vehicleType = getVehicleTypeById(typeId);
        return vehicleType?.name || '車種不明';
      }
      return '車種不明';
    }

    // 4. レガシー対応: vehicleModelが文字列の場合
    if (caseData.vehicleModel && !caseData.vehicleModel.match(/^[0-9a-f-]+$/)) {
      // 文字列として車両名が入っている場合、車種を推定
      // 暫定的に「普通車」を返す（実際の車種マッピングが必要）
      return '普通車';
    }

    // 5. レガシー対応: vehicleModelがUUIDの場合
    if (caseData.vehicleModel) {
      // UUID形式の場合は車両モデルマスタから検索
      const model = getVehicleModelById(caseData.vehicleModel);
      if (model) {
        const vehicleType = getVehicleTypeById(model.vehicleTypeId);
        return vehicleType?.name || '車種不明';
      }
    }

    return '車種不明';
  };

  return (
    <div>
      <div className='flex items-center justify-between mb-4'>
        <div>
          <PageTitle icon={DocumentTextIcon} title='案件詳細' />
          {caseData?.caseNumber && (
            <p className='text-sm text-blue-600 dark:text-blue-400 font-medium mt-2'>
              案件番号: {caseData.caseNumber}
            </p>
          )}
        </div>
      </div>

      <div className='mb-6 flex items-center justify-between'>
        <Button
          onClick={handleGoBack}
          variant='ghost'
          size='sm'
          className='text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
        >
          <ArrowLeftIcon className='h-4 w-4 mr-2' />
          戻る
        </Button>

        <div className='flex items-center space-x-3'>
          {canEditCase && (
            <>
              <Button
                onClick={() => navigate(`/cases/${caseData.id}/edit`)}
                variant='primary'
                size='sm'
                title='編集'
              >
                <PencilIcon className='h-4 w-4' />
              </Button>
              <Button
                onClick={() => handleDeleteCase(caseData.id, getVehicleName(caseData))}
                variant='danger'
                size='sm'
                title='削除'
              >
                <TrashIcon className='h-4 w-4' />
              </Button>
            </>
          )}
          <Badge
            variant={
              caseData.status === 'completed'
                ? 'success'
                : caseData.status === 'in_transit'
                  ? 'info'
                  : caseData.status === 'assigned'
                    ? 'warning'
                    : 'secondary'
            }
            size='sm'
          >
            {getCaseStatusText(caseData.status)}
          </Badge>
        </div>
      </div>

      {/* AcceptanceStatus badge removed - no longer needed */}

      <h1 className='text-3xl font-bold text-gray-800 dark:text-white mb-8'>
        案件詳細: {getVehicleName(caseData)}
      </h1>

      <div className='grid grid-cols-1 lg:grid-cols-2 gap-8'>
        {/* 基本情報 */}
        <DetailCard title='基本情報' icon={<DocumentTextIcon className='h-6 w-6' />}>
          <DetailItem
            icon={<TruckIcon className='h-5 w-5' />}
            label='車両名'
            value={getVehicleName(caseData)}
          />
          <DetailItem
            icon={<TruckIcon className='h-5 w-5' />}
            label='ナンバープレート番号'
            value={renderVehiclePlateNumber({
              vehiclePlateNumber: caseData.vehiclePlateNumber,
              vehiclePlateNumberNone: caseData.vehiclePlateNumberNone,
              size: 'sm',
              showDescription: true,
            })}
          />
          <DetailItem
            icon={<TruckIcon className='h-5 w-5' />}
            label='車体番号'
            value={caseData.chassisNumber || '未設定'}
          />
          <DetailItem
            icon={<TruckIcon className='h-5 w-5' />}
            label='車種'
            value={getVehicleType()}
          />
          <DetailItem
            icon={<UserIcon className='h-5 w-5' />}
            label='依頼者'
            value={caseData.requester || '-'}
          />
          <DetailItem
            icon={<DocumentTextIcon className='h-5 w-5' />}
            label='管理番号'
            value={caseData.managementNumber || '-'}
          />
        </DetailCard>

        {/* 取引先情報 */}
        <DetailCard title='取引先情報' icon={<BuildingOffice2Icon className='h-6 w-6' />}>
          <DetailItem
            icon={<BuildingOffice2Icon className='h-5 w-5' />}
            label='請求先'
            value={getPartnerNameByRole(caseData, 'billing')}
          />
          <DetailItem
            icon={<BuildingOffice2Icon className='h-5 w-5' />}
            label='引取先'
            value={getPartnerNameByRole(caseData, 'pickup')}
          />
          <DetailItem
            icon={<BuildingOffice2Icon className='h-5 w-5' />}
            label='搬入先'
            value={getPartnerNameByRole(caseData, 'delivery')}
          />
        </DetailCard>

        {/* 引取情報 */}
        <DetailCard title='引取情報' icon={<CalendarDaysIcon className='h-6 w-6' />}>
          <DetailItem
            icon={<CalendarDaysIcon className='h-5 w-5' />}
            label='引取日'
            value={caseData.pickupDate}
          />
          <DetailItem
            icon={<CalendarDaysIcon className='h-5 w-5' />}
            label='引取時間'
            value={`${formatTimeToHHMM(caseData.pickupTimeStart)} - ${formatTimeToHHMM(caseData.pickupTimeEnd)}`}
          />
          <DetailItem
            icon={<BuildingOffice2Icon className='h-5 w-5' />}
            label='引取住所'
            value={getPickupAddress()}
          />
          <DetailItem
            icon={<DocumentTextIcon className='h-5 w-5' />}
            label='引取連絡先'
            value={getPickupPhone()}
          />
        </DetailCard>

        {/* 搬入情報 */}
        <DetailCard title='搬入情報' icon={<CalendarDaysIcon className='h-6 w-6' />}>
          <DetailItem
            icon={<CalendarDaysIcon className='h-5 w-5' />}
            label='搬入日'
            value={caseData.deliveryDate}
          />
          <DetailItem
            icon={<CalendarDaysIcon className='h-5 w-5' />}
            label='搬入時間'
            value={formatTimeToHHMM(caseData.deliveryTime)}
          />
          <DetailItem
            icon={<BuildingOffice2Icon className='h-5 w-5' />}
            label='搬入住所'
            value={getDeliveryAddress()}
          />
          <DetailItem
            icon={<DocumentTextIcon className='h-5 w-5' />}
            label='搬入連絡先'
            value={getDeliveryPhone()}
          />
        </DetailCard>

        {/* 担当者情報 */}
        <DetailCard
          title='担当者情報'
          icon={
            !caseData.assignedLandTransporterId ? (
              <ExclamationTriangleIcon className='h-6 w-6 text-red-500' />
            ) : (
              <UserIcon className='h-6 w-6' />
            )
          }
        >
          {!caseData.assignedLandTransporterId ? (
            <div className='bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4'>
              <div className='flex items-center mb-2'>
                <ExclamationTriangleIcon className='h-5 w-5 text-red-500 mr-2' />
                <span className='text-red-700 dark:text-red-400 font-medium'>
                  陸送員の割り当てが必要です
                </span>
              </div>
              <p className='text-sm text-red-600 dark:text-red-400 mb-3'>
                この案件にはまだ陸送員が割り当てられていません。配送を実行するために担当者を指定してください。
              </p>
              {canEditCase && (
                <Button
                  onClick={() => navigate(`/cases/${caseData.id}/edit`)}
                  variant='danger'
                  size='sm'
                >
                  <PencilIcon className='h-4 w-4 mr-2' />
                  陸送員を割り当てる
                </Button>
              )}
            </div>
          ) : (
            <DetailItem
              icon={<UserIcon className='h-5 w-5' />}
              label='担当陸送員'
              value={getTransporterName(caseData.assignedLandTransporterId)}
            />
          )}
        </DetailCard>

        {/* 料金情報 - FL-208: 陸送員（DRIVER）には表示しない */}
        {canViewPricing && (
          <DetailCard title='料金情報' icon={<CurrencyYenIcon className='h-6 w-6' />}>
            <DetailItem
              icon={<CurrencyYenIcon className='h-5 w-5' />}
              label='陸送費'
              value={formatCurrencySafe(caseData.transportFee)}
            />
          </DetailCard>
        )}

        {/* FL-113: 車検状態管理 */}
        {(caseData.isVehicleInspectionExpired || caseData.requiresTemporaryNumber) && (
          <DetailCard
            title='車検状態'
            icon={<ExclamationTriangleIcon className='h-6 w-6 text-yellow-500' />}
          >
            <div className='space-y-4'>
              <VehicleInspectionStatus
                isExpired={caseData.isVehicleInspectionExpired}
                requiresTemporaryNumber={caseData.requiresTemporaryNumber}
                temporaryNumberNotes={caseData.temporaryNumberNotes}
                size='large'
                variant='detailed'
                className='w-full'
              />
            </div>
          </DetailCard>
        )}

        {/* 備考 */}
        <DetailCard title='備考' icon={<DocumentTextIcon className='h-6 w-6' />}>
          <DetailItem
            icon={<DocumentTextIcon className='h-5 w-5' />}
            label='備考'
            value={caseData.remarks || '備考なし'}
          />
          <DetailItem
            icon={<DocumentTextIcon className='h-5 w-5' />}
            label='案件ID'
            value={<span className='font-mono text-sm'>{caseData.id}</span>}
          />
        </DetailCard>
      </div>

      {/* 削除確認モーダル */}
      <DeleteConfirmModal
        isOpen={deleteConfirm?.show || false}
        itemName={deleteConfirm?.vehicleName || ''}
        itemType='案件'
        onConfirm={confirmDeleteCase}
        onCancel={() => setDeleteConfirm(null)}
        isDeleting={isDeleting}
      />
    </div>
  );
};
